<template>
  <div
    class="rounded border border-solid overflow-hidden"
    :class="showHighlightedBorder ? 'border-info' : 'border-gray-lighter'"
  >
    <div class="font-bold" :class="headerColors">
      <slot name="header" :toggle-collapsed="toggleCollapsed">
        <button type="button" class="w-full px-4 py-2 text-left" @click="toggleCollapsed">
          <span class="flex justify-between items-center gap-x-2">
            <span :id="facetLabelId" class="uppercase break-words">
              <template v-if="facetLabel">
                {{ facetLabel }}
              </template>
            </span>

            <span class="flex-none">
              <span class="text-[20px]">
                <template v-if="isCollapsed">+</template>
                <template v-else>-</template>
              </span>
            </span>
          </span>

          <span
            v-if="showSelectedRefinementsWhenCollapsed && isCollapsed && selectedFacetOptions"
            class="font-normal text-sm text-gray transform-none truncate"
          >
            {{ selectedFacetOptions }}
          </span>
        </button>
      </slot>
    </div>

    <div v-show="!isCollapsed" class="bg-white">
      <slot :is-collapsed="isCollapsed">
        <div
          class="px-4 py-2 text-sm space-y-4 lg:overflow-x-hidden lg:overflow-y-auto lg:max-h-[500px] text-black"
          role="group"
          :aria-labelledby="facetLabelId"
        >
          <slot name="options" :options="facetOptions">
            <BaseFormCheckboxRaw
              v-for="facetOption in facetOptions"
              :key="facetOption.value"
              v-model:checked="facetOption.isSelected"
              :label="facetOption.label"
              @update:checked="toggleFacetValue(facetOption.value, !facetOption.isSelected)"
            />
          </slot>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Facet } from '@constructor-io/constructorio-client-javascript'

const { label, facet, options, isLight, isCollapsedDefault, showHighlightedBorderWhenRefined } = defineProps<{
  label?: string
  facet?: Partial<Facet>
  options?: { label: string; value: string }[]
  isLight?: boolean
  isCollapsedDefault?: boolean
  showSelectedRefinementsWhenCollapsed?: boolean
  showHighlightedBorderWhenRefined?: boolean
}>()

const { instance, updateSearch } = useConstructorInject()

const isCollapsed = ref(isCollapsedDefault)

const headerColors = computed(() => {
  return ['text-dark', isLight ? 'bg-white' : 'bg-gray-lighter']
})

const facetLabel = computed(() => label ?? facet?.display_name)
const facetLabelId = useId()

// This is where we get the facet values in our current instance.
// If we just use the state response then we would be waiting for the network
// request to resolve before we could show a checkmark in the option's checkbox
const values = computed(() => instance.getFacetValues(facet?.name || ''))

const facetOptions = computed(() => {
  return (options ?? facet?.options ?? []).map((option) => ({
    label: option.label ?? option.display_name,
    value: option.value,
    isSelected: values.value.includes(option.value),
  }))
})

const selectedFacetOptions = computed(() => {
  return facetOptions.value
    .filter((option) => option.isSelected)
    .map((option) => option.value)
    .join(', ')
})

const showHighlightedBorder = computed(() => {
  return !!(showHighlightedBorderWhenRefined && selectedFacetOptions.value)
})

function toggleCollapsed() {
  isCollapsed.value = !isCollapsed.value
}

function toggleFacetValue(value: string, isSelected: boolean) {
  if (isSelected) instance.removeFacetValue(facet?.name || '', value)
  else instance.addFacetValue(facet?.name || '', value)

  updateSearch()
}
</script>
